import { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import 'components/Mapa.scss';

// Mesmo centro padrão usado como fallback em MapaView.js (São Paulo)
const LOCALIZACAO_PADRAO = { lat: -23.5505, lng: -46.6333 };

function lerPosicaoMarker(posicao) {
  if (!posicao) return null;
  return {
    lat: typeof posicao.lat === 'function' ? posicao.lat() : posicao.lat,
    lng: typeof posicao.lng === 'function' ? posicao.lng() : posicao.lng
  };
}

export default function ModalSelecionarLocalizacao({ isOpen, lat, lng, onConfirm, onClose }) {
  const mapRef = useRef();
  const markerRef = useRef(null);
  const [mapaCarregado, setMapaCarregado] = useState(false);
  const [centroInicial, setCentroInicial] = useState(null);
  const [posicaoSelecionada, setPosicaoSelecionada] = useState(null);
  // Só vira true depois que a transição de entrada do Modal (fade + transform
  // do Bootstrap) termina. É crucial não criar o <gmp-map> (mapa vetorial via
  // WebGL) enquanto o container ainda está em transição de transform/opacity:
  // o canvas WebGL fica preto permanentemente se inicializado nesse meio-tempo,
  // e nenhum resize/redraw posterior consegue recuperá-lo.
  const [transicaoConcluida, setTransicaoConcluida] = useState(false);

  // Ao abrir o modal, define o centro inicial: localização já preenchida no
  // form (edição) ou geolocalização do navegador (novo), com fallback fixo.
  useEffect(() => {
    if (!isOpen) {
      setMapaCarregado(false);
      setCentroInicial(null);
      setPosicaoSelecionada(null);
      setTransicaoConcluida(false);
      return;
    }

    const temLocalizacaoPreenchida = lat != null && lng != null && lat !== '' && lng !== '';
    if (temLocalizacaoPreenchida) {
      const posicao = { lat: parseFloat(lat), lng: parseFloat(lng) };
      setCentroInicial(posicao);
      setPosicaoSelecionada(posicao);
    } else if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const posicao = { lat: position.coords.latitude, lng: position.coords.longitude };
          setCentroInicial(posicao);
          setPosicaoSelecionada(posicao);
        },
        () => {
          console.log('Não foi possível obter sua localização');
          setCentroInicial(LOCALIZACAO_PADRAO);
          setPosicaoSelecionada(LOCALIZACAO_PADRAO);
        }
      );
    } else {
      console.log('Geolocalização não suportada');
      setCentroInicial(LOCALIZACAO_PADRAO);
      setPosicaoSelecionada(LOCALIZACAO_PADRAO);
    }

    let cancelled = false;
    customElements.whenDefined('gmp-map').then(() => {
      if (!cancelled) setMapaCarregado(true);
    });

    return () => { cancelled = true; };
  }, [isOpen, lat, lng]);

  // Centraliza o mapa e cria o marcador arrastável quando tudo estiver pronto.
  // Depende de `transicaoConcluida` porque o <gmp-map> só é montado no DOM
  // (ver JSX abaixo) depois que o Modal termina a transição de entrada.
  useEffect(() => {
    if (!isOpen || !transicaoConcluida || !mapaCarregado || !centroInicial) return;

    let cancelled = false;
    let clickListener = null;
    let timeoutId = null;

    // O elemento <gmp-map> já está conectado ao DOM neste ponto, mas o
    // google.maps.Map interno (`innerMap`) pode ser inicializado de forma
    // assíncrona pelo Web Component. Em vez de desistir silenciosamente caso
    // ainda não exista, tenta novamente por um curto período.
    const tentarInicializar = (tentativasRestantes = 20) => {
      if (cancelled) return;

      const innerMap = mapRef.current?.innerMap;
      if (!innerMap) {
        if (tentativasRestantes <= 0) return;
        timeoutId = setTimeout(() => tentarInicializar(tentativasRestantes - 1), 50);
        return;
      }

      innerMap.setOptions({
        mapTypeControl: false,
        center: centroInicial,
        zoom: 16
      });
      // Defensivo: garante que o mapa recalcule seu layout já com o container
      // no tamanho final (o Modal já terminou a transição neste ponto, mas o
      // resize também cobre qualquer resíduo de reflow tardio do navegador).
      window.google.maps.event.trigger(innerMap, 'resize');

      window.google.maps.importLibrary('marker').then(({ AdvancedMarkerElement, PinElement }) => {
        if (cancelled) return;

        const pin = new PinElement({
          background: '#2dce89',
          borderColor: '#2dce89',
          glyphColor: '#FFFFFF',
          scale: 1.1
        });

        const marker = new AdvancedMarkerElement({
          map: innerMap,
          position: centroInicial,
          content: pin.element,
          gmpDraggable: true
        });

        marker.addListener('dragend', () => {
          setPosicaoSelecionada(lerPosicaoMarker(marker.position));
        });

        markerRef.current = marker;
      });

      // Clique em outro ponto do mapa reposiciona o marcador
      clickListener = innerMap.addListener('click', (e) => {
        const novaPosicao = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setPosicaoSelecionada(novaPosicao);
        if (markerRef.current) {
          markerRef.current.position = novaPosicao;
        }
      });
    };

    tentarInicializar();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }
      if (clickListener) window.google.maps.event.removeListener(clickListener);
    };
  }, [isOpen, transicaoConcluida, mapaCarregado, centroInicial]);

  const handleConfirmar = () => {
    if (!posicaoSelecionada) return;
    onConfirm && onConfirm(posicaoSelecionada.lat, posicaoSelecionada.lng);
    onClose && onClose();
  };

  return (
    <Modal isOpen={!!isOpen} toggle={onClose} size="lg" onOpened={() => setTransicaoConcluida(true)}>
      <ModalHeader toggle={onClose}>Selecionar localização no mapa</ModalHeader>
      <ModalBody>
        <p className="text-muted">
          Arraste o marcador ou clique em outro ponto do mapa para posicionar o cruzamento.
        </p>
        <div className="mapa-google mapa-google-modal">
          {/* Só monta o <gmp-map> (WebGL/vetorial, via map-id) depois que a
              transição de entrada do Modal termina (onOpened acima) — criar o
              elemento enquanto o container ainda está em transform/opacity
              deixa o canvas preto permanentemente. */}
          {transicaoConcluida && (
            <gmp-map
              ref={mapRef}
              map-id="d5026c55ab90d3d1636ea361"
              center="-23.5505,-46.6333"
              zoom="11"
            ></gmp-map>
          )}
        </div>
        {posicaoSelecionada && (
          <p className="text-muted mt-2 mb-0">
            Latitude: {posicaoSelecionada.lat.toFixed(5)} | Longitude: {posicaoSelecionada.lng.toFixed(5)}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="button" color="primary" onClick={handleConfirmar} disabled={!posicaoSelecionada}>
          Usar esta localização
        </Button>
        <Button type="button" color="secondary" outline onClick={onClose}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
